import { useParams, Link } from "react-router-dom"
import { useEffect, useState, useContext, useRef } from "react"
import { CartContext } from "../context/CartContext"
import { getFamilyDisplayInfo, getProductColor, groupProductsByFamily } from "../utils/productFamily"
import "./CategoryPage.css"

function CategoryPage() {
  const { category } = useParams() // URL se slug milta hai: 'gpo-lace' ya 'buti'

  const [products, setProducts] = useState([])
  const [catName, setCatName] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [sort, setSort] = useState("price")
  const [currentPage, setCurrentPage] = useState(1)
  const productsTopRef = useRef(null)

  const { addToCart } = useContext(CartContext)

  const API_BASE = "https://data.lacegota.in/wp-json/wc/store/v1"
  const ITEMS_PER_PAGE = 20
  const BLANK_IMAGE =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3C/svg%3E"

  const getProductImageOptions = (product) => {
    const urls = [
      ...(product.images || []).map((image) => image.src),
      ...((product.familyVariants || []).flatMap((variant) =>
        (variant.images || []).map((image) => image.src)
      ))
    ].filter(Boolean)

    return [...new Set(urls)]
  }

  const addRetryParam = (url, retryCount) => {
    if (!url || url === BLANK_IMAGE) return BLANK_IMAGE

    const separator = url.includes("?") ? "&" : "?"
    return `${url}${separator}retry=${retryCount}`
  }

  useEffect(() => {
    const fetchStrictData = async () => {
      try {
        setLoading(true)
        setError("")
        setCurrentPage(1)
        
        // 1. Sabse pehle sari categories fetch karo taaki ID mil sake
        const catRes = await fetch(`${API_BASE}/products/categories?per_page=100`)
        if (!catRes.ok) throw new Error("Categories could not be loaded.")
        const allCats = await catRes.json()

        if (!Array.isArray(allCats)) throw new Error("Invalid categories response.")

        // URL slug ko categories list mein dhundo
        const currentCat = allCats.find(
          (c) => c.slug.toLowerCase().trim() === category.toLowerCase().trim()
        )

        if (!currentCat) {
          setProducts([])
          setCatName(category.replace(/-/g, " "))
          setLoading(false)
          return
        }

        setCatName(currentCat.name)

        // 2. Products fetch karo Category ID ka use karke
        let productURL = `${API_BASE}/products?category=${currentCat.id}&per_page=100`

        if (sort === "price") {
          productURL += "&orderby=price&order=asc"
        } else if (sort === "popularity") {
          productURL += "&orderby=popularity"
        }

        const prodData = []
        let page = 1
        let pageData = []

        do {
          const prodRes = await fetch(`${productURL}&page=${page}`)
          if (!prodRes.ok) throw new Error("Products could not be loaded.")
          pageData = await prodRes.json()
          if (!Array.isArray(pageData)) throw new Error("Invalid products response.")
          prodData.push(...pageData)
          page += 1
        } while (pageData.length === 100)

        if (prodData.length > 0) {
          // 3. STRICT FILTERING (Yahi Mix Category aur Empty issue fix karega)
          // Hum sirf wahi products dikhayenge jinki category list mein hamara URL wala slug hai
          const filtered = prodData.filter((p) => {
            return p.categories.some(
              (c) => c.slug.toLowerCase().trim() === category.toLowerCase().trim()
            )
          })

          // Agar filtering ke baad 0 results hain par API ne data bheja tha, 
          // toh iska matlab filter zyada strict hai, tab hum pure prodData ko dikhayenge
          const visibleProducts = filtered.length > 0 ? filtered : prodData
          setProducts(groupProductsByFamily(visibleProducts))
        } else {
          setProducts([])
        }
      } catch (err) {
        console.error("Fetch Error:", err)
        setProducts([])
        setError("Products could not be loaded. Please refresh the page.")
      } finally {
        setLoading(false)
      }
    }

    fetchStrictData()
  }, [category, sort])

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE)
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE
  const visibleProducts = products.slice(pageStart, pageStart + ITEMS_PER_PAGE)

  const changePage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return

    setCurrentPage(page)
    productsTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)

  return (
    <div className="categoryPage">
      {/* SORTING AREA 
      <div className="filterArea">
        <div className="filterWrapper">
          <button className={`chip ${sort === "" ? "active" : ""}`} onClick={() => setSort("")}>
            Default
          </button>
          <button className={`chip ${sort === "popularity" ? "active" : ""}`} onClick={() => setSort("popularity")}>
            Best Selling
          </button>
          <button className={`chip ${sort === "price" ? "active" : ""}`} onClick={() => setSort("price")}>
            Price: Low to High
          </button>
        </div>
      </div>*/}

      {/* HEADER */}
      <div className="categoryHeader">
        <h2>{catName}</h2>
        <span className="productCount">{products.length} Items found</span>
      </div>

      {/* PRODUCT LIST */}
      <main className="categoryProducts" ref={productsTopRef}>
        {loading ? (
          <div className="loadingContainer"><div className="spinner"></div><p>Loading {catName}...</p></div>
        ) : error ? (
          <div className="noProducts">
            <h3>{error}</h3>
          </div>
        ) : products.length === 0 ? (
          <div className="noProducts">
            <h3>No products found in {catName}.</h3>
            <p>Please check another category or refresh the page.</p>
          </div>
        ) : (
          <div className="trendingGrid">
            {visibleProducts.map((p, index) => {
              // Image Check: agar first load fail ho, same image retry karo,
              // phir same family ke next variant image par fallback karo.
              const imageOptions = getProductImageOptions(p)
              const img = imageOptions[0] || BLANK_IMAGE
              
              // Price Logic
              const saleP = p.prices?.price ? Number(p.prices.price) / 100 : 0
              const regP = p.prices?.regular_price ? Number(p.prices.regular_price) / 100 : saleP
              const disc = regP > saleP ? Math.round(((regP - saleP) / regP) * 100) : 0

              const color = getProductColor(p)
              const familyVariants = p.familyVariants || [p]
              const familyDisplay = getFamilyDisplayInfo(p, familyVariants)
              const variantCount = familyDisplay.displayCount
              const variantType = familyDisplay.displayLabel

              return (
                <div className="amazonProductCard" key={p.id}>
                  <Link to={`/product/${p.slug}`} className="cardLink">
                    <div className="cardImgBox">
                      {disc > 10 && <div className="bestSellerBadge">Best Seller</div>}
                      <img
                        src={img}
                        alt={p.name}
                        loading={index < 8 ? "eager" : "lazy"}
                        decoding="async"
                        onError={(event) => {
                          const imageElement = event.currentTarget
                          const retryCount = Number(imageElement.dataset.retryCount || 0)
                          const imageIndex = Number(imageElement.dataset.imageIndex || 0)

                          if (retryCount < 2 && imageOptions[imageIndex]) {
                            imageElement.dataset.retryCount = String(retryCount + 1)
                            setTimeout(() => {
                              imageElement.src = addRetryParam(imageOptions[imageIndex], retryCount + 1)
                            }, 350)
                            return
                          }

                          const nextImage = imageOptions[imageIndex + 1]
                          if (nextImage) {
                            imageElement.dataset.imageIndex = String(imageIndex + 1)
                            imageElement.dataset.retryCount = "0"
                            imageElement.src = nextImage
                            return
                          }

                          imageElement.onerror = null
                          imageElement.src = BLANK_IMAGE
                          imageElement.style.visibility = "hidden"
                        }}
                      />
                    </div>
                  </Link>

                  <div className="cardInfo">
                    <Link to={`/product/${p.slug}`} className="titleLink">
                      <h4 className="pName">{p.name}</h4>
                    </Link>
                    <div className="availableBadge">
                      Available in {variantCount} {variantType}
                    </div>
                    <div className="pMeta">Color: {color}</div>
                    <div className="pRating">⭐⭐⭐⭐ 4.5</div>

                    <div className="priceContainer">
                      <div className="mainPriceRow">
                        {disc > 0 && <span className="pPercentage">-{disc}%</span>}
                        <span className="pCurrency">₹</span>
                        <span className="pPriceBold">{saleP}</span>
                      </div>
                      {regP > saleP && (
                        <div className="mktPriceRow">
                          M.R.P.: <span className="pStrike">₹{regP}</span>
                        </div>
                      )}
                    </div>

                    <button className="amazonAddBtn" onClick={() => addToCart({ ...p, familyVariants: undefined, price: saleP }, 1)}>
                      Add to Cart
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <nav className="pagination" aria-label="Category pages">
            <button
              type="button"
              className="paginationNav"
              onClick={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="paginationNumbers">
              {pageNumbers.map((page) => (
                <button
                  type="button"
                  key={page}
                  className={`paginationPage ${page === currentPage ? "active" : ""}`}
                  onClick={() => changePage(page)}
                  aria-current={page === currentPage ? "page" : undefined}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="paginationNav"
              onClick={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </nav>
        )}
      </main>
    </div>
  )
}

export default CategoryPage

